import React, { useMemo, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Breadcrumbs, Button, Chip, CircularProgress, Dialog, DialogContent,
  Grid, IconButton, Stack, Tab, Tabs, Tooltip, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import SceneCard from '../../features/production-review/components/SceneCard';
import VersionsDrawer from '../../features/production-review/components/VersionsDrawer';
import BatchActions from '../../features/production-review/components/BatchActions';
import { useProductionReview } from '../../features/production-review/hooks/useProductionReview';
import { REVIEW_TABS } from '../../features/production-review/types/production-review';
import { runProduction } from '../../services/contentAiService';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

function PlaceholderTab({ label }) {
  return (
    <Box
      sx={{
        minHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 4, textAlign: 'center'
      }}
    >
      <Stack spacing={1} alignItems="center">
        <Chip size="small" label="Em desenvolvimento" color="warning" variant="filled" />
        <Typography variant="h6" color="text.secondary">
          Aba {label}
        </Typography>
        <Typography variant="body2" color="text.disabled" maxWidth={520}>
          Esta aba será implementada nas próximas fases da Central de Aprovação.
          Atualmente, apenas a aba de Imagens está funcional.
        </Typography>
      </Stack>
    </Box>
  );
}

export default function ProductionReviewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const productionId = Number(id || 0) || 0;
  const review = useProductionReview(productionId);
  const [activeTab, setActiveTab] = useState('images');
  const [starting, setStarting] = useState(false);

  const { scenes = [], counts = {}, summary = {} } = review.reviewState || {};

  const canStartPipeline = useMemo(() => {
    if (starting) return false;
    if (!summary?.total) return false;
    return Boolean(summary?.allApproved);
  }, [summary, starting]);

  const onStartPipeline = async () => {
    if (!productionId || !canStartPipeline) return;
    setStarting(true);
    try {
      await runProduction(productionId);
      toast.success('Pipeline retomado. Montagem do vídeo iniciada.');
      setTimeout(() => navigate(`/production/${String(productionId)}`), 900);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao iniciar');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" spacing={1.5} useFlexGap>
        <Stack spacing={1}>
          <Breadcrumbs aria-label="breadcrumb">
            <RouterLink to="/production" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography color="text.secondary" variant="body2">Produções</Typography>
            </RouterLink>
            <RouterLink to={`/production/${String(productionId)}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography color="text.secondary" variant="body2">Detalhe #{String(productionId)}</Typography>
            </RouterLink>
            <Typography variant="body2">Central de Aprovação</Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1.5} useFlexGap alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={() => navigate(`/production/${String(productionId)}`)}
            >Voltar</Button>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Production Review Center
            </Typography>
            <Chip size="small" label={`Produção #${String(productionId)}`} variant="outlined" />
            <Tooltip title="Atualizar dados">
              <IconButton size="small" onClick={() => review?.loadReview?.({ silent: false }).catch(() => {})}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canStartPipeline && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={starting}
                startIcon={starting ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                onClick={onStartPipeline}
              >
                {starting ? 'Iniciando...' : 'Iniciar Montagem do Vídeo'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          {REVIEW_TABS.map((tab) => (
            <Tab
              key={String(tab.key)}
              value={String(tab.key)}
              label={tab.enabled ? tab.label : `${tab.label} (em breve)`}
              disabled={!tab.enabled}
              sx={{ opacity: tab.enabled ? 1 : 0.55 }}
            />
          ))}
        </Tabs>
      </Box>

      {activeTab === 'images' && (
        <Stack spacing={2.5}>
          {review.loading && !scenes.length ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress />
                <Typography color="text.secondary" variant="body2">Carregando cenas...</Typography>
              </Stack>
            </Box>
          ) : (
            <>
              <BatchActions review={review} counts={counts} summary={summary} />

              {!scenes.length ? (
                <Box
                  sx={{
                    p: 6, border: '1px dashed', borderColor: 'divider', borderRadius: 2,
                    textAlign: 'center'
                  }}
                >
                  <Typography color="text.secondary">
                    Nenhuma cena encontrada para esta produção.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {scenes.map((scene) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={String(scene.id)}>
                      <SceneCard scene={scene} review={review} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </Stack>
      )}

      {activeTab !== 'images' && (
        <PlaceholderTab label={REVIEW_TABS.find((t) => t.key === activeTab)?.label || activeTab} />
      )}

      <VersionsDrawer
        scene={review.versionDrawerScene || null}
        review={review}
        onClose={() => review?.setVersionDrawerScene?.(null)}
      />

      <Dialog
        open={Boolean(review.previewImage)}
        onClose={() => review?.setPreviewImage?.(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#000' } }}
      >
        <DialogContent sx={{ p: 0, m: 0, position: 'relative' }}>
          <Box
            component="img"
            src={review.previewImage || ''}
            alt="preview"
            sx={{ width: '100%', height: 'auto', display: 'block', maxHeight: '80vh', objectFit: 'contain' }}
          />
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: '#fff' }}
            onClick={() => review?.setPreviewImage?.(null)}
          >
            <CloseIcon />
          </IconButton>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
